import type { BaseQueryApi, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { AppDispatch, IRootState } from "../../../store";
import {
  openapi,
  type PostProjectByProjectIdReportboardsAndReportIdFieldApiArg as FieldArgs,
  type PostProjectByProjectIdReportboardsAndReportIdCommentApiArg as CommentArgs,
  type PostProjectByProjectIdReportboardsAndReportIdFieldApiResponse as ReportResult,
} from "../../../../openapi";
import { reportRTK } from "./reportRTK";
import { applyDirectReportResult, getDirectReportVersion } from "./applyDirectReportResult";
import { createOptimisticReportFields } from "./optimisticReportFields";
import { createDirectReportQueue } from "./directReportQueue";

type Args = FieldArgs | CommentArgs;
const cacheKey = (arg: Args) => ({ projectId: arg.projectId, reportBoardId: arg.reportId });
const createSession = (revision: number) => ({
  queue: createDirectReportQueue(revision),
  patches: createOptimisticReportFields(),
  active: 0,
  conflict: false,
});
// Separate queues per report and Redux store; no copy of report data is kept here.
const sessions = new WeakMap<() => unknown, Map<string, ReturnType<typeof createSession>>>();
const sessionFor = (arg: Args, getState: () => unknown) => {
  let reports = sessions.get(getState);
  if (!reports) sessions.set(getState, (reports = new Map()));
  const key = JSON.stringify(cacheKey(arg));
  let session = reports.get(key);
  if (!session) reports.set(key, (session = createSession(arg.body.expectedRevision)));
  return {
    session,
    release: () => {
      if (--session.active === 0) reports.delete(key);
    },
  };
};

type Lifecycle = {
  dispatch: BaseQueryApi["dispatch"];
  getState: () => unknown;
  queryFulfilled: Promise<{ data: ReportResult }>;
};
async function patchReport(arg: Args, { dispatch, getState, queryFulfilled }: Lifecycle) {
  const { session, release } = sessionFor(arg, getState);
  session.active++;
  const update = (recipe: Parameters<typeof reportRTK.util.updateQueryData<"getSingleReportBoard">>[2]) =>
    (dispatch as AppDispatch)(reportRTK.util.updateQueryData("getSingleReportBoard", cacheKey(arg), recipe));
  let patchId: number | undefined;
  if ("value" in arg.body) {
    const { path, value } = arg.body;
    update((board) => {
      const version = getDirectReportVersion(board);
      if (version) patchId = session.patches.add(version.data, path, value);
    });
  }
  try {
    const { data } = await queryFulfilled;
    update((board) => {
      applyDirectReportResult(board, data);
      const version = getDirectReportVersion(board);
      if (version) session.patches.accept(version.data, patchId);
    });
  } catch {
    if (patchId !== undefined)
      update((board) => {
        const version = getDirectReportVersion(board);
        if (version) session.patches.reject(version.data, patchId);
      });
  } finally {
    release();
  }
}

// Reuse the generated request builder, but serialize transport so queued requests
// use the revision returned by the preceding field/comment write.
function enhanceReportMutation<Arg extends Args>(endpoint: { query?: (arg: Arg) => string | FetchArgs }) {
  const query = endpoint.query;
  Object.assign(endpoint, {
    query: undefined,
    onQueryStarted: patchReport,
    async queryFn(
      arg: Arg,
      api: BaseQueryApi,
      _extraOptions: unknown,
      baseQuery: (args: string | FetchArgs) => PromiseLike<{ data?: unknown; error?: FetchBaseQueryError }>,
    ) {
      const { session } = sessionFor(arg, api.getState);
      const board = reportRTK.endpoints.getSingleReportBoard.select(cacheKey(arg))(api.getState() as IRootState).data;
      session.queue.syncRevision(board?.revision ?? arg.body.expectedRevision);
      try {
        const data = await session.queue.enqueue(async (expectedRevision) => {
          if (session.conflict) throw { status: 409, data: { message: "Rapporten er endret. Last inn på nytt." } };
          const result = await baseQuery(query({ ...arg, body: { ...arg.body, expectedRevision } }));
          if (result.error) {
            if (result.error.status === 409) session.conflict = true;
            throw result.error;
          }
          return result.data as ReportResult;
        });
        return { data };
      } catch (error) {
        return { error: error as FetchBaseQueryError };
      }
    },
  });
}

export const reportApi = openapi.enhanceEndpoints({
  endpoints: {
    postProjectByProjectIdReportboardsAndReportIdField: enhanceReportMutation<FieldArgs>,
    postProjectByProjectIdReportboardsAndReportIdComment: enhanceReportMutation<CommentArgs>,
  },
});

export const {
  usePostProjectByProjectIdReportboardsAndReportIdFieldMutation,
  usePostProjectByProjectIdReportboardsAndReportIdCommentMutation,
} = reportApi;
